import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Camera, ChevronLeft, ChevronRight, Download, Save, Trash2, UploadCloud } from "lucide-react";
import { HomeworkShell } from "./HomeworkShell";
import { hwApi, hwErr, fmtDate } from "./hwApi";

const OBJECTIVE = ["mcq", "assertion_reason", "multi_statement"];

function AssignmentInner() {
  const { assignmentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [uploading, setUploading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [textAnswers, setTextAnswers] = useState({});
  const [cur, setCur] = useState(0);
  const [mode, setMode] = useState("solve"); // solve | review | done
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const load = () => hwApi.get(`/assignments/${assignmentId}`).then(({ data: d }) => {
    setData(d);
    const draft = d.submission?.draft;
    if (draft) {
      setAnswers(draft.answers || {});
      setTextAnswers(draft.text_answers || {});
      if (draft.saved_at) setNotice(`Progress restored — saved ${fmtDate(draft.saved_at)}.`);
    }
  }).catch((e) => setError(hwErr(e)));
  useEffect(() => { load(); }, [assignmentId]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setError("");
    try {
      const form = new FormData();
      for (const f of files) form.append("files", f);
      await hwApi.post(`/assignments/${assignmentId}/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setNotice(`${files.length} file(s) uploaded.`);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) { setError(hwErr(err)); } finally { setUploading(false); }
  };

  const removeFile = async (index) => {
    try {
      await hwApi.delete(`/assignments/${assignmentId}/files/${index}`);
      await load();
    } catch (err) { setError(hwErr(err)); }
  };

  const saveDraft = async () => {
    setSaving(true);
    setError("");
    try {
      const { data: r } = await hwApi.put(`/assignments/${assignmentId}/draft`, { answers, text_answers: textAnswers });
      setNotice(`Progress saved — ${r.answered} question(s) answered. You can safely leave and resume later.`);
      setTimeout(() => setNotice(""), 4000);
    } catch (err) { setError(hwErr(err)); } finally { setSaving(false); }
  };

  const submit = async () => {
    if (saving) return;  // double-click guard — backend also 409s duplicates
    setSaving(true);
    setError("");
    try {
      const { data: r } = await hwApi.post(`/assignments/${assignmentId}/submit`, { answers, text_answers: textAnswers });
      setMode("done");
      await load();
      return r;
    } catch (err) { setError(hwErr(err)); setMode("solve"); return null; } finally { setSaving(false); }
  };

  const confirmSubmit = () => {
    if (window.confirm("Submit homework? Answers are saved and files will be locked while Pranjal Sir evaluates them.")) submit();
  };

  if (error && !data) return <div className="tp-wrap"><p className="tp-error">{error}</p></div>;
  if (!data) return <div className="tp-wrap"><p className="tp-muted">Loading…</p></div>;

  const sub = data.submission;
  const ev = data.evaluation;
  const editable = data.status === "assigned" || data.status === "correction_requested";
  const qs = data.questions || [];
  const answeredCount = qs.filter((q) =>
    (OBJECTIVE.includes(q.qtype) && answers[q.question_id]) ||
    (!OBJECTIVE.includes(q.qtype) && (textAnswers[q.question_id] || "").trim())).length;
  const q = qs[cur];
  const ir = sub?.instant_result;
  const irWrong = ir ? ir.attempted - ir.correct : 0;
  const irSkipped = ir ? ir.total_objective - ir.attempted : 0;

  return (
    <div className="tp-wrap" style={{ maxWidth: 760 }} data-testid="hw-assignment">
      <div className="tp-eyebrow">HOMEWORK · {data.assignment_id}</div>
      <h1 className="tp-h2" style={{ fontSize: 28 }}>{data.title}</h1>
      <p className="tp-muted" data-testid="hw-asg-meta">
        {data.category_label || ""}{data.chapter ? ` · ${data.chapter}` : ""}{data.topic && data.chapter !== "Daily Practice" ? ` · ${data.topic}` : ""}{data.subtopic ? ` · ${data.subtopic}` : ""}
        {" "}· {data.question_count} questions · Assigned {fmtDate(data.created_at)} · Due {fmtDate(data.deadline)}{data.late ? " · submitted late" : ""}</p>
      {data.instructions && <p className="tp-note" style={{ background: "#eff6ff", padding: 12, borderRadius: 10, color: "#1e3a5f" }} data-testid="hw-instructions">{data.instructions}</p>}
      {error && <p className="tp-error" role="alert" data-testid="hw-asg-error">{error}</p>}
      {notice && <p className="tp-pill ok" data-testid="hw-asg-notice">{notice}</p>}
      {data.status === "correction_requested" && ev && (
        <div className="tp-panel" style={{ border: "1px solid #f59e0b", marginTop: 12 }} data-testid="hw-correction-note">
          <b style={{ color: "#fbbf24" }}>Teacher asked for corrections</b>
          <p className="tp-muted" style={{ margin: "6px 0 0" }}>{ev.feedback || "Please redo and re-upload your solutions."}</p>
        </div>
      )}

      {/* ---- submission confirmation screen ---- */}
      {mode === "done" && (
        <div className="tp-panel" style={{ marginTop: 14, border: "2px solid #12b76a", background: "#f0fdf4", textAlign: "center", padding: 24 }} data-testid="hw-submit-done">
          <CheckCircle2 size={40} style={{ color: "#12b76a", margin: "0 auto" }} />
          <h2 className="tp-h2" style={{ fontSize: 22, margin: "10px 0 4px" }}>Homework submitted!</h2>
          <p className="tp-muted" style={{ margin: 0 }}>Your answers are saved and locked. Submitted {fmtDate(Date.now() / 1000)}.</p>
          {ir && (
            <div className="tp-grid-4" style={{ marginTop: 14 }} data-testid="hw-done-breakdown">
              {[[ir.correct, "Correct", "#15803d"], [irWrong, "Incorrect", "#b42318"], [irSkipped, "Unanswered", "#b45309"], [`${ir.percent}%`, "Accuracy", "#1d4ed8"]].map(([v, l, c]) => (
                <div className="tp-card" key={l} style={{ textAlign: "center" }}>
                  <b style={{ fontSize: 22, color: c }}>{v}</b>
                  <p className="tp-muted" style={{ margin: 0, fontSize: 12 }}>{l}</p>
                </div>
              ))}
            </div>
          )}
          <p className="tp-muted" style={{ fontSize: 13, marginTop: 12 }}>Instant score covers auto-checkable MCQs only. Pranjal Sir will personally check your handwritten work and publish final marks with error analysis.</p>
        </div>
      )}

      {/* ---- upload panel (handwritten work) ---- */}
      <div className="tp-panel" style={{ marginTop: 14, border: "2px dashed #1d4ed8", background: "#f0f6ff" }}>
        <h2 className="tp-h2" style={{ fontSize: 18 }}>Submit your handwritten solutions</h2>
        <p className="tp-muted" style={{ fontSize: 13 }}>Solve all questions in your notebook, then upload clear photos (JPG/PNG) or one combined PDF.</p>
        {sub?.files?.length > 0 && (
          <div style={{ margin: "10px 0" }} data-testid="hw-file-list">
            {sub.files.map((f) => (
              <div className="admin-row" key={f.index} style={{ padding: "6px 0", borderTop: "1px solid #dbe7f7" }} data-testid={`hw-file-${f.index}`}>
                <span style={{ flex: 1, color: "#1e3a5f", fontSize: 13 }}>{f.name} <small className="tp-muted">· {(f.size / 1024).toFixed(0)} KB</small></span>
                <a className="tp-btn small ghost" href={`${process.env.REACT_APP_BACKEND_URL}/api/hw/files/${sub.submission_id}/${f.index}`} target="_blank" rel="noreferrer" data-testid={`hw-file-view-${f.index}`}><Download size={13} /></a>
                {editable && !sub.locked && <button className="tp-btn small ghost" onClick={() => removeFile(f.index)} data-testid={`hw-file-del-${f.index}`}><Trash2 size={13} /></button>}
              </div>
            ))}
          </div>
        )}
        {editable && (
          <>
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf" multiple onChange={(e) => upload(Array.from(e.target.files))} data-testid="hw-upload-input" style={{ display: "none" }} />
            <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={(e) => upload(Array.from(e.target.files))} data-testid="hw-camera-input" style={{ display: "none" }} />
            <button type="button" className="tp-btn ghost" style={{ width: "100%", padding: 18, fontSize: 15, border: "1.5px solid #1d4ed8", background: "#fff" }}
                    disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="hw-upload-btn">
              <UploadCloud size={20} /> {uploading ? "Uploading…" : "UPLOAD PHOTOS / PDF"}
            </button>
            <button type="button" className="tp-btn ghost" style={{ width: "100%", padding: 14, fontSize: 14, marginTop: 8, border: "1.5px dashed #1d4ed8", background: "#fff" }}
                    disabled={uploading} onClick={() => camRef.current?.click()} data-testid="hw-camera-btn">
              <Camera size={18} /> TAKE PHOTO (phone camera)
            </button>
            <p className="tp-note" style={{ textAlign: "center", margin: "6px 0 0" }}>JPG, JPEG, PNG or PDF · up to 10 files · 15 MB each</p>
          </>
        )}
        {data.status === "submitted" && <p className="tp-pill ok" data-testid="hw-under-eval"><UploadCloud size={14} /> Submitted — under evaluation by Pranjal Sir</p>}
      </div>

      {/* ---- solving UI (stepper) ---- */}
      {editable && mode === "solve" && q && (
        <div className="tp-panel" style={{ marginTop: 14 }} data-testid="hw-solve-panel">
          <div className="admin-row" style={{ justifyContent: "space-between" }}>
            <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>Question {cur + 1} of {qs.length}</h2>
            <span className="tp-muted" style={{ fontSize: 12 }} data-testid="hw-progress-text">{answeredCount} answered · {qs.length - answeredCount} remaining</span>
          </div>
          <div style={{ height: 6, background: "#e4eaf2", borderRadius: 6, marginTop: 8, overflow: "hidden" }}>
            <div style={{ width: `${(answeredCount / Math.max(1, qs.length)) * 100}%`, height: "100%", background: "#1d4ed8", transition: "width .3s ease" }} data-testid="hw-progress-bar" />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }} data-testid="hw-qnav">
            {qs.map((qq, i) => {
              const answered = (OBJECTIVE.includes(qq.qtype) && answers[qq.question_id]) || (!OBJECTIVE.includes(qq.qtype) && (textAnswers[qq.question_id] || "").trim());
              return (
                <button key={qq.question_id} type="button" onClick={() => setCur(i)} data-testid={`hw-qnav-${i + 1}`}
                        style={{ width: 34, height: 34, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                                 border: i === cur ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                                 background: answered ? "#dcfce7" : "#fff", color: "#0b2545" }}>
                  {i + 1}
                </button>
              );
            })}
          </div>

          <div style={{ borderTop: "1px solid #e4eaf2", marginTop: 12, paddingTop: 12 }} data-testid={`hw-q-${q.qno}`}>
            <div className="admin-row">
              <span className="status-pill review">{q.qtype.replace(/_/g, " ")}</span>
              <span className="tp-muted" style={{ fontSize: 12 }}>{q.difficulty} · {q.max_marks} marks</span>
            </div>
            <p style={{ color: "#0b2545", margin: "8px 0 0", whiteSpace: "pre-wrap", fontSize: 15 }}>{q.text}</p>
            {OBJECTIVE.includes(q.qtype) && q.options && Object.keys(q.options).length > 0 ? (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }} data-testid={`hw-options-${q.qno}`}>
                {Object.entries(q.options).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => setAnswers((c) => ({ ...c, [q.question_id]: k }))}
                          data-testid={`hw-opt-${q.qno}-${k}`}
                          style={{ textAlign: "left", padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14,
                                   border: answers[q.question_id] === k ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                                   background: answers[q.question_id] === k ? "#eff6ff" : "#fff", color: "#0b2545" }}>
                    <b>{k})</b> {v}
                  </button>
                ))}
              </div>
            ) : (
              <textarea rows={4} value={textAnswers[q.question_id] || ""}
                        onChange={(e) => setTextAnswers((c) => ({ ...c, [q.question_id]: e.target.value }))}
                        placeholder={q.qtype === "numerical" ? "Write the formula, substitution with values, and final answer with units…" : "Type your answer here — key points, reasoning, conclusion…"}
                        data-testid={`hw-text-answer-${q.qno}`}
                        style={{ width: "100%", marginTop: 10, padding: 12, borderRadius: 10, border: "1.5px solid #cbd5e1", fontSize: 14, color: "#0b2545", resize: "vertical" }} />
            )}
          </div>

          <div className="admin-row" style={{ marginTop: 14, justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <button type="button" className="tp-btn ghost" disabled={cur === 0} onClick={() => setCur((c) => c - 1)} data-testid="hw-prev-btn"><ChevronLeft size={15} /> Previous</button>
            <button type="button" className="tp-btn ghost" onClick={saveDraft} disabled={saving} data-testid="hw-save-draft"><Save size={15} /> {saving ? "Saving…" : "Save progress"}</button>
            {cur < qs.length - 1 ? (
              <button type="button" className="tp-btn ghost" onClick={() => setCur((c) => c + 1)} data-testid="hw-next-btn">Next <ChevronRight size={15} /></button>
            ) : (
              <button type="button" className="tp-btn" onClick={() => setMode("review")} data-testid="hw-review-btn">Review & submit <ChevronRight size={15} /></button>
            )}
          </div>
        </div>
      )}

      {/* ---- review screen ---- */}
      {editable && mode === "review" && (
        <div className="tp-panel" style={{ marginTop: 14 }} data-testid="hw-review-panel">
          <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>Review your answers</h2>
          <p className="tp-muted" style={{ fontSize: 13 }}>{answeredCount} of {qs.length} answered · {sub?.files?.length || 0} handwritten file(s) uploaded</p>
          {qs.map((qq, i) => {
            const ans = OBJECTIVE.includes(qq.qtype) ? (answers[qq.question_id] || "") : (textAnswers[qq.question_id] || "").trim();
            return (
              <div key={qq.question_id} className="admin-row" style={{ borderTop: "1px solid #e4eaf2", padding: "8px 0" }} data-testid={`hw-review-q-${i + 1}`}>
                <span className={`status-pill ${ans ? "approved" : "failed"}`}>{ans ? "ANSWERED" : "EMPTY"}</span>
                <span style={{ flex: 1, color: "#1e3a5f", fontSize: 13 }}>Q{i + 1}. {qq.text.slice(0, 80)}{qq.text.length > 80 ? "…" : ""}</span>
                <b style={{ color: "#1d4ed8", fontSize: 13, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {OBJECTIVE.includes(qq.qtype) ? (ans || "—") : (ans ? `${ans.slice(0, 40)}${ans.length > 40 ? "…" : ""}` : "—")}</b>
                <button type="button" className="tp-btn small ghost" onClick={() => { setCur(i); setMode("solve"); }} data-testid={`hw-review-edit-${i + 1}`}>Edit</button>
              </div>
            );
          })}
          <div className="admin-row" style={{ marginTop: 14, gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="tp-btn ghost" onClick={() => setMode("solve")} data-testid="hw-review-back"><ChevronLeft size={15} /> Back to questions</button>
            <button type="button" className="tp-btn" disabled={!sub?.files?.length} onClick={confirmSubmit} data-testid="hw-final-submit">
              <CheckCircle2 size={16} /> SUBMIT HOMEWORK
            </button>
          </div>
          {!sub?.files?.length && <p className="tp-note" style={{ color: "#b42318", marginTop: 8 }} data-testid="hw-review-nofiles">Upload at least one photo/PDF of your handwritten work above before submitting.</p>}
        </div>
      )}

      {/* ---- read-only questions after submission ---- */}
      {!editable && (
        <div className="tp-panel" style={{ marginTop: 14 }}>
          <h2 className="tp-h2" style={{ fontSize: 18 }}>Questions</h2>
          {qs.map((qq) => {
            const em = ev?.per_question?.find((m) => m.question_id === qq.question_id);
            const irEntry = ir?.per_question?.find((p) => p.question_id === qq.question_id);
            const typed = sub?.text_answers?.[qq.question_id];
            return (
              <div key={qq.question_id} style={{ borderTop: "1px solid #e4eaf2", padding: "12px 0" }} data-testid={`hw-q-${qq.qno}`}>
                <div className="admin-row">
                  <span className="status-pill locked">Q{qq.qno}</span>
                  <span className="status-pill review">{qq.qtype.replace(/_/g, " ")}</span>
                  <span className="tp-muted" style={{ fontSize: 12 }}>{qq.difficulty} · {qq.max_marks} marks</span>
                  {em && <b style={{ color: "#15803d" }} data-testid={`hw-q-marks-${qq.qno}`}>{em.awarded}/{em.max_marks}</b>}
                </div>
                <p style={{ color: "#0b2545", margin: "8px 0 0", whiteSpace: "pre-wrap" }}>{qq.text}</p>
                {qq.options && Object.keys(qq.options).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {Object.entries(qq.options).map(([k, v]) => {
                      const chosen = irEntry?.chosen === k;
                      return (
                        <p key={k} className="tp-muted" style={{ margin: "2px 0", ...(chosen ? { color: "#0b2545", fontWeight: 600 } : {}) }}
                           data-testid={chosen ? `hw-chosen-${qq.qno}` : undefined}>
                          {k}) {v} {chosen && (irEntry?.is_correct ? "✓ your choice" : "✗ your choice")}
                        </p>
                      );
                    })}
                  </div>
                )}
                {typed && <p className="tp-note" style={{ marginTop: 6, background: "#f8fafc", padding: 8, borderRadius: 8 }} data-testid={`hw-typed-${qq.qno}`}><b>Your answer:</b> {typed}</p>}
                {em?.qstatus && (
                  <div className="admin-row" style={{ marginTop: 6, gap: 6 }} data-testid={`hw-q-status-${qq.qno}`}>
                    <span className={`status-pill ${em.qstatus === "correct" ? "approved" : em.qstatus === "partially_correct" ? "review" : "failed"}`}>
                      {em.qstatus.replace(/_/g, " ").toUpperCase()}</span>
                    {em.error_category && <span className="status-pill" style={{ background: "#fef3c7", color: "#92400e" }}>{em.error_category}</span>}
                  </div>
                )}
                {em?.comment && <p className="tp-note" style={{ color: "#92400e", marginTop: 6 }}>Pranjal Sir: {em.comment}</p>}
                {ev && data.evaluation?.release_solutions && qq.solution && (
                  <div className="tp-note" style={{ color: "#0369a1", marginTop: 6, background: "#f0f9ff", padding: 10, borderRadius: 8 }} data-testid={`hw-solution-${qq.qno}`}>
                    {qq.formula && <p style={{ margin: "0 0 4px" }}><b>Formula:</b> {qq.formula}</p>}
                    <p style={{ margin: 0 }}><b>Solution:</b> {qq.solution}</p>
                    {qq.explanation && <p style={{ margin: "4px 0 0" }}><b>Concept:</b> {qq.explanation}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {ir && mode !== "done" && (
        <div className="tp-panel" style={{ marginTop: 14, border: "1px solid #7dd3fc", background: "#f0f9ff" }} data-testid="hw-instant-result">
          <div className="tp-eyebrow">INSTANT MCQ CHECK</div>
          <b style={{ fontSize: 22, color: "#0369a1" }} data-testid="hw-instant-score">
            {ir.correct}/{ir.total_objective} objective correct · {ir.percent}%</b>
          <p className="tp-muted" style={{ margin: "6px 0 0", fontSize: 13 }}>
            Auto-checked from the options you selected — correct answers stay hidden for now. Final marks,
            error categories and feedback will come personally from Pranjal Sir (NOT AI).</p>
        </div>
      )}

      {ev && ev.status === "published" && (
        <div className="tp-panel" style={{ marginTop: 14, border: "1px solid #12b76a" }} data-testid="hw-result-card">
          <div className="tp-eyebrow">RESULT</div>
          <b style={{ fontSize: 26, color: "#15803d" }} data-testid="hw-result-score">{ev.total}/{ev.max_total} · {ev.percentage}%</b>
          {ev.feedback && <p className="tp-muted" style={{ marginTop: 8 }} data-testid="hw-result-feedback">{ev.feedback}</p>}
          <a className="tp-btn small" style={{ marginTop: 10, textDecoration: "none", display: "inline-flex" }}
             href={`${process.env.REACT_APP_BACKEND_URL}/api/hw/assignments/${data.assignment_id}/report.pdf`}
             data-testid="hw-report-download"><Download size={14} /> Download report card (PDF)</a>
        </div>
      )}
    </div>
  );
}

export default function HomeworkAssignment() {
  return <HomeworkShell><AssignmentInner /></HomeworkShell>;
}
